/**
 * COMPOSITION — the mix of kinds, as a donut, a grouped bar or a 100% stack.
 *
 * ── THE FOUR-SLOT CAP, AND WHY IT IS NOT NEGOTIABLE HERE ──
 *
 * `dataViz.series` is validated on ADJACENT pairs — slot n against slot n+1 —
 * which is the right measurement for a line chart or a grouped bar, where a
 * reader compares neighbours. A donut is the other case: every wedge is compared
 * against every other. Under ALL-PAIRS validation the order holds to FOUR slots
 * and fails hard at five (green against orange collapses to ΔE 5.5 under
 * deuteranopia).
 *
 * So the top four kinds take consecutive slots 0-3 and everything else folds
 * into ONE neutral "Other" wedge. The folded kinds are named in the tooltip and
 * in the key, so nothing disappears — the fifth kind is still readable, it just
 * is not given a colour that ~8% of men cannot separate from another wedge.
 *
 * ── ONE PANEL, THREE FORMS ──
 *
 * The brief asks for a stacked / grouped / percentage toggle rather than three
 * separate panels. The cap applies to all three: a 100%-stacked bar is the same
 * all-pairs comparison as a donut.
 */
import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  Sector,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AnalyticsBatchResult } from '../../../../../api/analytics-client';
import { palette } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { useCompareMeaning } from '../charts/compare-label';
import { ChartTable } from '../charts/chart-table-view';
import { DashSimpleTooltip, DashTooltip } from '../charts/chart-tooltip';
import { Slice, foldWithMembers } from '../charts/chart-palette';
import {
  BAR_RADIUS,
  cursor,
  format,
  grid,
  margins,
  markSeparator,
  xAxisCategory,
  yAxisCount,
  firstPaintAnimation,
} from '../charts/chart-theme';
import { PanelToggle } from '../charts/chart.styled';
import { KeyItem, KeyRow, KeySwatch } from './panels.styled';
import { readSlot, rowsFromItems, slotState, substitutionCaveat } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';

const PANEL_ID = 'composition';

/* FOUR READS OF ONE BREAKDOWN, and each answers a different question:
     donut   — what is the mix
     grouped — which kind is biggest, read as a length rather than an angle
     stacked — how the whole is built, and with a second scope chosen, how two
               places are built DIFFERENTLY (two columns, same total height only
               if the totals match, which is itself the finding)
     100%    — the shares alone, with volume deliberately removed
   The toggle replaces what would otherwise be four panels of the same data. */
type Form = 'donut' | 'grouped' | 'stacked' | 'percent';

/** Hover emphasis: a 4px outer ring rather than the recharts default, which
 *  pops the wedge out of the ring and makes two adjacent wedges look like
 *  different sizes. */
/* eslint-disable @typescript-eslint/no-explicit-any */
const ActiveSector = (props: any) => (
  <g>
    <Sector {...props} />
    <Sector
      {...props}
      innerRadius={props.outerRadius + 3}
      outerRadius={props.outerRadius + 6}
      fill={props.fill}
      opacity={0.5}
    />
  </g>
);
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface CompositionPanelProps {
  state: ChartShellState;
  result?: AnalyticsBatchResult;
  title?: string;
  isMobile: boolean;
  drill: DrillHandlers;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const CompositionPanel: React.FC<CompositionPanelProps> = ({
  state,
  result,
  title = 'Composition',
  isMobile,
  drill,
  onExportPng,
  onExportCsv,
}) => {
  const [form, setForm] = useState<Form>('donut');
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const compareMeaning = useCompareMeaning();

  const slot = readSlot(result);
  const data = slot.ok;

  const slices: Slice[] = useMemo(
    () =>
      data
        ? foldWithMembers(
            data.items.map(item => ({
              key: item.key,
              label: item.label,
              value: format.numberOf(item.value) ?? 0,
            }))
          )
        : [],
    [data]
  );

  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const noun = data?.noun || ['record', 'records'];

  // Direct labels are legal only at four wedges or fewer (the design system's
  // rule), and on a phone the leader lines overlap into a smear at any count —
  // so the key underneath carries the labels there.
  const directLabels = slices.length <= 4 && !isMobile;

  const percentData = slices.map(slice => ({
    ...slice,
    value: total > 0 ? Math.round((slice.value / total) * 1000) / 10 : 0,
  }));

  const controls = (
    <>
      {(['donut', 'grouped', 'stacked', 'percent'] as Form[]).map(option => (
        <PanelToggle
          key={option}
          type="button"
          active={form === option}
          aria-pressed={form === option}
          data-testid={`composition-${option}`}
          onClick={() => setForm(option)}
        >
          {option === 'donut'
            ? 'Donut'
            : option === 'grouped'
            ? 'Bars'
            : option === 'stacked'
            ? 'Stack'
            : '100%'}
        </PanelToggle>
      ))}
    </>
  );

  const chart = () => {
    if (form === 'donut') {
      return (
        <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
          <Tooltip
            content={
              <DashSimpleTooltip
                tone={palette.iris}
                valueKey="value"
                labelKey="label"
                noun={noun}
                total={total}
                note={row =>
                  (row.folded as string[])?.length ? (
                    <span data-testid="folded-kinds">
                      Folded in: {(row.folded as string[]).join(', ')}
                    </span>
                  ) : null
                }
              />
            }
          />
          <Pie
            data={slices}
            dataKey="value"
            nameKey="label"
            /* PERCENTAGES, not pixels: a fixed 90/130px ring clips on a
               phone-width canvas. */
            innerRadius="52%"
            outerRadius="78%"
            paddingAngle={1}
            /* A 2px surface gap between adjacent segments, so two similar wedges
               never bleed into one shape. */
            stroke={palette.surface}
            strokeWidth={2}
            label={directLabels ? (entry: { label?: string }) => String(entry?.label ?? '') : false}
            labelLine={directLabels}
            activeIndex={activeIndex}
            activeShape={ActiveSector}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
            onClick={drill.onMark}
            cursor="pointer"
            {...firstPaintAnimation(PANEL_ID)}
          >
            {slices.map(slice => (
              <Cell key={slice.key} fill={slice.tone} />
            ))}
          </Pie>
        </PieChart>
      );
    }

    /* THE STACK IS A DIFFERENT SHAPE OF DATA: one row per scope, one KEY per
       kind — the transpose of the other three forms, which have one row per
       kind. Building it here rather than in a second panel is what lets the
       toggle be a toggle. */
    if (form === 'stacked') {
      const column: Record<string, string | number> = { label: 'This period' };
      for (const slice of slices) column[slice.key] = slice.value;

      const compareColumn: Record<string, string | number> | null = slices.some(
        s2 => s2.compareValue != null
      )
        ? (() => {
            const row: Record<string, string | number> = {
              label: compareMeaning.label,
            };
            for (const slice of slices) row[slice.key] = slice.compareValue ?? 0;

            return row;
          })()
        : null;

      const columns = compareColumn ? [column, compareColumn] : [column];

      return (
        <BarChart accessibilityLayer data={columns} margin={margins.default} barCategoryGap="34%">
          <CartesianGrid {...grid} />
          <XAxis dataKey="label" {...xAxisCategory} />
          <YAxis {...yAxisCount} />
          <Tooltip
            content={
              <DashTooltip
                series={slices.map(slice => ({
                  key: slice.key,
                  label: slice.label,
                  tone: slice.tone,
                }))}
                total={total}
              />
            }
            cursor={cursor.bar}
          />
          {slices.map((slice, index) => (
            <Bar
              key={slice.key}
              dataKey={slice.key}
              name={slice.label}
              stackId="mix"
              fill={slice.tone}
              {...markSeparator}
              /* Only the TOP segment rounds, or every band in the stack gets a
                 rounded shoulder and the column reads as a pile of separate
                 bars rather than one whole. */
              radius={index === slices.length - 1 ? BAR_RADIUS : undefined}
              onClick={drill.onMark}
              cursor="pointer"
              {...firstPaintAnimation(`${PANEL_ID}-stack`)}
            />
          ))}
        </BarChart>
      );
    }

    const rows = form === 'percent' ? percentData : slices;

    return (
      <BarChart accessibilityLayer data={rows} margin={margins.default} barCategoryGap="28%">
        <CartesianGrid {...grid} />
        <XAxis dataKey="label" {...xAxisCategory} />
        <YAxis
          {...yAxisCount}
          allowDecimals={form === 'percent'}
          tickFormatter={form === 'percent' ? (v: number) => `${v}%` : undefined}
        />
        <Tooltip
          content={
            <DashSimpleTooltip
              tone={palette.iris}
              valueKey="value"
              labelKey="label"
              noun={form === 'percent' ? ['%', '%'] : noun}
              total={form === 'percent' ? undefined : total}
            />
          }
          cursor={cursor.bar}
        />
        <Bar
          dataKey="value"
          radius={BAR_RADIUS}
          {...markSeparator}
          onClick={drill.onMark}
          cursor="pointer"
          {...firstPaintAnimation(`${PANEL_ID}-bars`)}
        >
          {rows.map(slice => (
            <Cell key={slice.key} fill={slice.tone} />
          ))}
        </Bar>
      </BarChart>
    );
  };

  return (
    <ChartShell
      id={PANEL_ID}
      title={title}
      aspect={form === 'donut' ? 1.5 : 2}
      minHeight={260}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat="The mix of kinds recorded in this period. The four largest are named; everything else is folded into Other, which the tooltip and the key name in full."
      extraCaveat={substitutionCaveat(data)}
      emptyReason={
        slot.empty
          ? {
              title: 'No kinds were recorded',
              text: 'Nothing in this period carried a kind, so there is no mix to show.',
            }
          : null
      }
      summary={
        slices.length
          ? `${title}: ${slices
              .map(s => `${s.label} ${s.value.toLocaleString()}`)
              .join(', ')}.`
          : undefined
      }
      controls={controls}
      /* THE KEY, UNDER THE WEDGES. The donut's own labels only fit four short
         names; the key is where "Other" says how many kinds it swallowed, which
         is the difference between a fold and a silent truncation. */
      footer={slices.length ? <CompositionKey slices={slices} total={total} /> : null}
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      table={
        data ? (
          <ChartTable
            dimensionLabel="Kind"
            valueLabel={data.label}
            rows={rowsFromItems(data)}
            showCompare={data.compareTotal != null}
          />
        ) : undefined
      }
    >
      {chart()}
    </ChartShell>
  );
};

/** The key doubles as the legend and as the numbers.
 *
 *  HOW MANY WERE FOLDED, on the key rather than only in the tooltip: with a
 *  dozen evenly-spread kinds "Other" can be the LARGEST wedge on the panel — the
 *  first browser pass on the analytics screen showed it at 48% — and a wedge
 *  labelled only "Other" then looks like the answer instead of like seven
 *  answers stacked. */
export const CompositionKey: React.FC<{ slices: Slice[]; total: number }> = ({
  slices,
  total,
}) => (
  <KeyRow data-testid="composition-key">
    {slices.map(slice => (
      <KeyItem key={slice.key}>
        <KeySwatch tone={slice.tone} aria-hidden="true" />
        {slice.label}
        {slice.folded.length ? ` (${slice.folded.length} more)` : ''}{' '}
        {slice.value.toLocaleString()} ({format.share(slice.value, total)})
      </KeyItem>
    ))}
  </KeyRow>
);
